import type { IAnalyticLineChartData } from '~/modules/campaign-analytics';

import { IVariantType } from '~/modules/campaign/types';
import { dayjsTzToLocalTZ } from '~/utils/dayjs';

const randomWithTrend = (base: number, trend: number, variance = 1) => {
  const trendFactor = 1 + (Math.random() - 0.5) * variance;
  return Math.floor(base * trendFactor * trend);
};

const generateDailyData = (params: {
  date: string;
  variantKey: IVariantType;
  baseMetrics: Record<string, number>;
  trend?: number;
}) => {
  const { date, variantKey, baseMetrics, trend = 1.05 } = params;
  const channels = [
    { totalCount: randomWithTrend(baseMetrics.direct || 80, trend), type: 'direct' },
    { totalCount: randomWithTrend(baseMetrics.organic || 50, trend), type: 'organic' },
    { totalCount: randomWithTrend(baseMetrics.social || 30, trend), type: 'social' },
  ];

  const visitors = [
    { totalCount: randomWithTrend(baseMetrics.newVisitors || 80, trend), type: 'new' },
    { totalCount: randomWithTrend(baseMetrics.returningVisitors || 40, trend), type: 'returning' },
  ];

  const devices = [
    { totalCount: randomWithTrend(baseMetrics.desktop || 100, trend), type: 'desktop' },
    { totalCount: randomWithTrend(baseMetrics.mobile || 50, trend), type: 'mobile' },
    { totalCount: randomWithTrend(baseMetrics.tablet || 20, trend), type: 'tablet' },
  ];

  return {
    date,
    totalSessions: randomWithTrend(baseMetrics.sessions || 150, trend),
    totalVisitors: randomWithTrend(baseMetrics.visitors || 120, trend),
    totalOrders: randomWithTrend(baseMetrics.orders || 20, trend),
    totalRevenue: randomWithTrend(baseMetrics.revenue || 1000, trend),
    totalPageViewed: randomWithTrend(baseMetrics.pageViews || 300, trend),
    totalAddedToCart: randomWithTrend(baseMetrics.addedToCart || 15, trend),
    totalTimeOnPage: randomWithTrend(baseMetrics.timeOnPage || 60000, trend),
    totalClickedHasHref: randomWithTrend(baseMetrics.clickedHasHref || 40, trend),
    totalClicked: randomWithTrend(baseMetrics.clicked || 60, trend),
    variantKey,
    channels,
    visitors,
    devices,
    totalPageViewedHaveTimeOnPage: randomWithTrend(baseMetrics.pageViewedWithTime || 200, trend),
    totalReachedCheckout: randomWithTrend(baseMetrics.reachedCheckout || 100, trend),
    totalSessionsThatCompletedCheckout: randomWithTrend(baseMetrics.completedCheckout || 50, trend),
    totalSessionsThatReachedAndCompletedCheckout: randomWithTrend(
      baseMetrics.reachedAndCompleted || 30,
      trend,
    ),
    totalSessionsThatReachedCheckout: randomWithTrend(baseMetrics.reachedCheckout || 70, trend),
    totalSessionsWithoutAction: randomWithTrend(baseMetrics.withoutAction || 10, trend),
    totalSessionsWithCartAdditions: randomWithTrend(baseMetrics.withCartAdditions || 100, trend),
  };
};

const generateVariantData = (params: {
  variantKey: IVariantType;
  startDate: string;
  days: number;
  baseMetrics: Record<string, number>;
  trend?: number;
}) => {
  const { variantKey, startDate, days, baseMetrics, trend = 1.05 } = params;
  const data = [];
  const start = dayjsTzToLocalTZ(startDate);

  for (let i = 0; i < days; i++) {
    const currentDate = start.add(i, 'day').format('YYYY-MM-DD');

    const currentTrend = Math.pow(trend, i);

    data.push(
      generateDailyData({ date: currentDate, variantKey, baseMetrics, trend: currentTrend }),
    );
  }

  return {
    variantKey,
    data,
  };
};

export const generateIntervalMetrics = (params: {
  startDate?: string;
  days?: number;
  variantATrend?: number;
  variantBTrend?: number;
  variantABaseMetrics: Record<string, number>;
  variantBBaseMetrics: Record<string, number>;
}): IAnalyticLineChartData['metricInterval'] => {
  const { variantABaseMetrics, variantBBaseMetrics } = params;
  const days = params.days || 7;
  const startDate = '2025-03-26';
  const variantATrend = params.variantATrend || 1.05;
  const variantBTrend = params.variantBTrend || 1.03;

  return [
    generateVariantData({
      variantKey: IVariantType.VARIANT_A,
      startDate,
      days,
      baseMetrics: variantABaseMetrics,
      trend: variantATrend,
    }),
    generateVariantData({
      variantKey: IVariantType.VARIANT_B,
      startDate,
      days,
      baseMetrics: variantBBaseMetrics,
      trend: variantBTrend,
    }),
  ];
};
